<template>
  <div class="flatfinder-leaflet-v1">
    <div
      class="flatfinder-leaflet-v1__aspect"
      :style="{ ...style, backgroundImage: `url(${image})` }"
    >
      <div ref="map" class="flatfinder-leaflet-v1__map content"></div>
      <div class="flatfinder-leaflet-v1__top-right">
        <slot name="top-right"></slot>
      </div>
      <div class="flatfinder-leaflet-v1__top-left">
        <slot name="top-left"></slot>
      </div>
      <div class="flatfinder-leaflet-v1__bottom-right">
        <slot name="bottom-right"></slot>
      </div>
      <div class="flatfinder-leaflet-v1__bottom-left">
        <slot name="bottom-left"></slot>
      </div>
    </div>
    <Card v-if="hover" :value="hover" :mouse="mouse" />
  </div>
</template>

<script>
import Leaflet from 'leaflet'
import 'leaflet/dist/leaflet.css'
import Card from './Card'
import Styles from './_colors.scss'
import BrowserApiMixin from './mixins/browser-api'

Leaflet.CRS.CustomZoom = Leaflet.extend({}, Leaflet.CRS.Simple)

export default {
  mixins: [BrowserApiMixin('flatfinder')],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    image: String,
    dimensions: Object,
    preventNavigation: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    image: 'init',
    value: 'render',
    $route: 'render',
  },
  data() {
    return {
      promise: null,
      map: null,
      layer: null,
      overlay: null,
      hover: null,
      mouse: { x: 0, y: 0 },
      mouseHandler: null,
      permanentTooltip: true,
    }
  },
  computed: {
    ratio() {
      return this.dimensions.height / this.dimensions.width
    },
    style() {
      let maxHeight = screen.availHeight - 300
      let maxWidth = Math.min(
        (this.dimensions.width / this.dimensions.height) * maxHeight,
        this.dimensions.width,
      )

      return {
        '--map-ratio': `${this.ratio * 100}%`,
        maxWidth: `${maxWidth}px`,
      }
    },
  },
  methods: {
    getColor(item, hover, type = '-bg') {
      let reference = item.reference
      if (this.$route.params.id === reference.id)
        return Styles[`current${type}${hover ? '-hover' : ''}`]

      switch (reference.onModel) {
        case 'Residential':
          return Styles[`${item.status.toLowerCase()}${type}${hover ? '-hover' : ''}`]
        default:
          return Styles[
            `${reference.onModel.toLowerCase()}-${item.status}${type}${hover ? '-hover' : ''}`
          ]
      }
    },
    render() {
      this.hover = null
      if (!this.layer) return
      this.layer.clearLayers()

      this.value.forEach((item, index) => {
        const isProjectOrFlatfinder = ['Project', 'Flatfinder', 'Link'].includes(
          item.reference.onModel,
        )

        let layer = Leaflet.GeoJSON.geometryToLayer(item.data, {
          weight: 1,
          color: this.getColor(item, false, '-border'),
          fillColor: this.getColor(item),
          fillOpacity: 0.3,
          opacity: this.getColor(item, false, '-border-opacity'),
          className: `reference-${item.reference.id} ${
            isProjectOrFlatfinder ? 'flatfinder-layer--animated' : ''
          } flatfinder-layer--delay-${index % 20}`,
        })

        layer._external_id = item.reference.id

        this.layer.addLayer(layer)

        if (this.$route.params.id === item.reference.id) {
          layer.bindTooltip(this.$t('youAreHere'), { permanent: true, direction: 'center' })
        }

        layer.on('mouseover', () => {
          layer.setStyle({
            fillColor: this.getColor(item, true),
            color: this.getColor(item, true, '-border'),
          })

          if (isProjectOrFlatfinder) {
            layer.getTooltip().getElement().classList.remove('flatfinder-tooltip--muted')
          }
        })

        layer.on('mouseout', () => {
          layer.setStyle({
            fillColor: this.getColor(item),
            color: this.getColor(item, false, '-border'),
          })

          if (isProjectOrFlatfinder) {
            layer.getTooltip().getElement().classList.add('flatfinder-tooltip--muted')
          }
        })

        if (isProjectOrFlatfinder) {
          layer.bindTooltip(item.reference.name || item.reference.label, {
            direction: 'center',
            permanent: this.permanentTooltip,
            className: 'flatfinder-tooltip--muted',
          })
        }

        if (['Residential'].includes(item.reference.onModel)) {
          layer.on('mouseover', () => {
            this.hover = item.reference
          })

          layer.on('mouseout', () => {
            this.hover = null
          })
        }

        layer.on('click', () => {
          this.$emit('click', item.reference)

          switch (item.reference.onModel) {
            case 'Residential':
              if (this.preventNavigation) return
              if (item.reference.redirectUrl)
                return (window.location.href = item.reference.redirectUrl)
              return this.$router.push({ name: 'Residential', params: { id: item.reference.id } })
            case 'Flatfinder':
              return this.$emit('switch', item.reference)
            case 'Project':
              if (this.preventNavigation) return
              return (window.location.href = item.reference.url)
            case 'Link':
              if (this.preventNavigation) return
              return (window.location.href = item.reference.href)
          }
        })
      })
    },
    destroyMap() {
      if (!this.map) return

      this.map.off()
      this.map.remove()
    },
    initMap() {
      if (this.map) this.destroyMap()

      this.map = Leaflet.map(this.$refs.map, {
        minZoom: -4,
        maxZoom: 4,
        center: [0, 0],
        zoom: 0,
        zoomSnap: 0.01,
        maxBoundsViscosity: 0.85,
        scrollWheelZoom: false,
        crs: Leaflet.CRS.CustomZoom,
      })

      this.layer = Leaflet.featureGroup()
      this.map.addLayer(this.layer)
    },
    init() {
      if (!this.image) return
      if (!this.map) this.initMap()
      if (this.layer) this.layer.clearLayers()

      let southWest = this.map.unproject([0, this.dimensions.height], 0)
      let northEast = this.map.unproject([this.dimensions.width, 0], 0)
      let bounds = new Leaflet.LatLngBounds(southWest, northEast)

      if (this.map.hasLayer(this.overlay)) this.map.removeLayer(this.overlay)
      this.overlay = Leaflet.imageOverlay(this.image, bounds).addTo(this.map)

      this.map.fitBounds(bounds, { animate: false })
      this.map.setMaxBounds(bounds, { animate: false })

      this.render()
    },
  },
  mounted() {
    this.mouseHandler = ({ clientX: x, clientY: y }) => {
      this.mouse.x = x
      this.mouse.y = y
    }
    document.addEventListener('mousemove', this.mouseHandler)
    this.init()
  },
  beforeDestroy() {
    this.destroyMap()
    document.removeEventListener('mousemove', this.mouseHandler)
  },
  components: {
    Card,
  },
}
</script>

<style lang="scss">
.flatfinder-leaflet-v1 {
  display: flex;
  justify-content: center;

  $spacing: 1rem;

  &__map {
    border-radius: $border-radius;
    overflow: hidden;
    background-color: white;
  }

  &__aspect {
    width: 100%;
    @include aspect-ratio(1, 1);
    background-color: css-alpha('primary', 0.1);
    background-size: contain;

    @include respond-below('phone') {
      margin-bottom: $spacing * 1.6;
    }

    &:before {
      padding-top: var(--map-ratio);
    }
  }

  &__bottom-right,
  &__bottom-left,
  &__top-right,
  &__top-left {
    position: absolute;
    z-index: 999;
  }

  &__top-right,
  &__top-left {
    top: $spacing;

    @include respond-below('phone') {
      top: 0;
    }
  }

  &__top-right {
    right: $spacing;

    @include respond-below('phone') {
      right: 0;
    }
  }

  &__top-left {
    left: $spacing;

    @include respond-below('phone') {
      left: 0;
    }
  }

  &__bottom-right,
  &__bottom-left {
    bottom: $spacing;

    @include respond-below('phone') {
      bottom: 0;
    }
  }

  &__bottom-right {
    right: $spacing;

    @include respond-below('phone') {
      right: 0;
    }
  }

  &__bottom-left {
    left: $spacing;

    @include respond-below('phone') {
      left: 0;
    }
  }

  .leaflet-control-attribution {
    display: none;
  }

  .leaflet-container {
    background-color: darken(white, 4%);
  }

  .flatfinder-layer--animated {
    animation: flatfinder-pulse 2s infinite;
  }

  @for $i from 0 to 20 {
    .flatfinder-layer--delay-#{$i} {
      animation-delay: 200ms * $i;
    }
  }

  .flatfinder-tooltip--muted {
    background-color: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(4px);
    border-color: transparent;
  }

  .elder-loader__element {
    z-index: 9999;
  }
}

@keyframes flatfinder-pulse {
  50% {
    fill-opacity: 0.5;
  }
}
</style>
